<template>

    <modal
        :title="formData.id ? 'Gruppe bearbeiten' : 'Gruppe hinzufügen'"
        transition="fadeDown"
        :show-ok="false"
        :show-cancel="false"
        :is-show="isShow"
        :backdrop-closable="false"
        @close="handleClose"
        class="vcenter"
        :width="600">

        <div class="brand-groups-modal">

            <div class="group-fields-container" v-if="!isLoading">
                
                <div class="fields-row">
                    <!-- Name -->
                    <div class="field-item">
                        <form-input
                            inputType="text"
                            label="SAM-ASSSET_GROUP-GROUP_NAME"
                            :is-horizontal="false"
                            :showInfoText="false"
                            v-model="formData.name"
                            placeholder="Name eingeben">
                        </form-input>
                    </div>

                    <!-- Kategorie -->
                    <div class="field-item">
                        <form-input
                            inputType="select"
                            :showLabel="true"
                            label="SAM-ASSET_GROUP-CATEGORY"
                            v-model="formData.category"
                            selectFieldName="name"
                            :selectIsSearchable="true"
                            :selectAllowEmpty="false"
                            :selectOptions="sanovaCategories"
                            :showLabelInOptions="false">
                        </form-input>
                    </div>
                </div>

                <!-- Description -->
                <div class="field">
                    <form-input
                        inputType="textarea"
                        :showLabel="true"
                        :fullWidth="true"
                        label="SAM-ASSET_GROUP-DESCRIPTION"
                        v-model="formData.description"
                        placeholder="Beschreibung eingeben">
                    </form-input>
                </div>

                <!-- Asset Upload -->
                <div class="field">
                    <lang labelKey="SAM-ASSSET_GROUP-IMAGE"></lang>
                    <div class="replace-content">
                        
                        <!-- Preview (Links) -->
                        <div class="preview-container">
                            <div v-if="formData.previewDataURL || formData.existingAssetUrl" 
                                 class="imgWrapper preview-box image-preview" 
                                 :style="{'background-image': 'url(' + (formData.previewDataURL ? formData.previewDataURL : formData.existingAssetUrl) + ')'}">
                            </div>
                            <div v-else class="imgWrapper preview-box placeholder-asset" style="display: flex; align-items: center; justify-content: center; background: #fafafa; border: 1px solid #efefef;">
                                <i class="fa fa-picture-o" style="color: #ccc; font-size: 24px;"></i>
                            </div>
                        </div>

                        <!-- Dropzone (Rechts) -->
                        <div class="dropzone-container">
                            <dropzone-wrapper
                                :key="dropzoneKey"
                                ref="sanovaGroupDropzone"
                                id="sanovaGroupDropzone"
                                url="fileProcessing/upload?preset=sanova&createAssets=1"
                                :auto-process-queue="true"
                                :max-files="1"
                                :max-filesize="16"
                                dz-class="dzSingle"
                                dict-default-message="Neues Bild hier ablegen oder klicken zum Auswählen"
                                acceptedFiles="image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
                                @vdropzone-file-added="onFileAdded"
                                @vdropzone-success="onFileSuccess"
                                @vdropzone-removed-file="onFileRemoved">
                            </dropzone-wrapper>
                        </div>

                    </div>
                </div>

            </div>

            <!-- Loading state -->
            <div v-if="isLoading" class="empty-state">
                <i class="fa fa-spinner fa-spin"></i>
                <p>Wird geladen...</p>
            </div>

        </div>

        <!-- Footer slot -->
        <div slot="footer">
            <button class="button" @click="handleClose" :disabled="isSaving">
                Abbrechen
            </button>
            <button
                class="button is-primary"
                :class="{ 'is-loading': isSaving }"
                :disabled="isSaving"
                @click="save">
                Speichern
            </button>
        </div>

    </modal>

</template>

<script>
import DropzoneWrapper from '@/components/utils/dropzoneWrapper.vue';

export default {
    name: 'AssetGroupsModal',

    components: {
        DropzoneWrapper
    },

    props: {
        isShow: {
            type: Boolean,
            default: false
        },
        group: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            formData: {
                id: null,
                name: '',
                description: '',
                category: null,
                newFile: null,
                uploadedAssetId: null,
                existingAssetUrl: null,
                existingAssetId: null,
                previewDataURL: null
            },
            isSaving: false,
            isLoading: false,
            sanovaCategories: [],
            dropzoneKey: 0
        };
    },

    watch: {
        isShow(val) {
            if (val) {
                this.isLoading = true;
                this.getSanovaCategories().then(() => {
                    if (this.group) {
                        let catId = null;
                        if (this.group.sanovaCategory) {
                            catId = this.group.sanovaCategory.id || this.group.sanovaCategory;
                        }
                        this.formData = {
                            id: this.group.id,
                            name: this.group.name || '',
                            description: this.group.description || '',
                            category: catId ? this.sanovaCategories.find(c => c.id == catId) : null,
                            newFile: null,
                            uploadedAssetId: null,
                            existingAssetUrl: this.group.asset ? this.getImagePath(this.group.asset) : null,
                            existingAssetId: this.group.asset ? this.group.asset.id : null,
                            previewDataURL: null
                        };
                    } else {
                        this.formData = {
                            id: null,
                            name: '',
                            description: '',
                            category: null,
                            newFile: null,
                            uploadedAssetId: null,
                            existingAssetUrl: null,
                            existingAssetId: null,
                            previewDataURL: null
                        };
                    }
                    this.dropzoneKey += 1;
                    this.isLoading = false;
                }).catch(() => {
                    this.isLoading = false;
                });
            } else {
                this.isSaving = false;
            }
        }
    },

    methods: {

        onFileAdded(file) {
            this.formData.newFile = file;
            this.formData.uploadedAssetId = null;
            if (file && file.type.match(/image.*/)) {
                let reader = new FileReader();
                reader.onload = (e) => {
                    this.formData.previewDataURL = e.target.result;
                };
                reader.readAsDataURL(file);
            } else {
                this.formData.previewDataURL = null;
            }
        },

        onFileSuccess(file, response) {
            let res = typeof response === 'string' ? JSON.parse(response) : response;
            this.formData.uploadedAssetId = res.id;

            // Clear dropzone immediately by incrementing key
            this.dropzoneKey += 1;
        },

        onFileRemoved() {
            this.formData.newFile = null;
            this.formData.uploadedAssetId = null;
            this.formData.previewDataURL = null;
        },

        async save() {
            // Validation: Ensure the group has a name and category
            const hasName = !!(this.formData.name && this.formData.name.trim().length > 0);
            const isUploading = !!(this.formData.newFile && !this.formData.uploadedAssetId);
            const hasCategory = !!(this.formData.category && this.formData.category.id);

            if (!hasName || isUploading || !hasCategory) {
                if (isUploading) {
                    this.$notify.open({ 
                        content: 'Dateiupload läuft noch. Bitte warten Sie, bis der Upload abgeschlossen ist.', 
                        type: 'warning' 
                    });
                    return;
                }

                this.$notify.open({ 
                    content: 'Bitte vergeben Sie einen Namen und wählen Sie eine Kategorie.', 
                    type: 'danger' 
                });
                return;
            }

            this.isSaving = true;

            try {
                let assetId = this.formData.uploadedAssetId || this.formData.existingAssetId || null;

                const payload = {
                    name: this.formData.name,
                    description: this.formData.description || '',
                    sanovaCategory: this.formData.category.id,
                    asset: assetId
                };

                if (this.formData.id) {
                    payload.id = this.formData.id;
                }

                await this.axios.post(this.$config.api + 'sanovaAssetGroup', payload);

                this.$notify.open({ content: 'Gruppe erfolgreich gespeichert!', type: 'success' });
                this.$emit('saved');
                this.handleClose();

            } catch (e) {
                this.$notify.open({ content: 'Fehler beim Speichern: ' + (e.message || 'Unbekannter Fehler'), type: 'danger' });
            } finally {
                this.isSaving = false;
            }
        },

        getImagePath(asset, thumb=false) {
            if(!asset) {
                return "";
            }
            let path = asset.path ? asset.path : 'dam/sanovaAssetGroup';
            return this.$config.cdnHost + "apokat-backend/assets/" + path + "/" + (thumb != false ? '150/' : '') + asset.filename;
        },

        getSanovaCategories() {
            return this.axios.get(this.$config.api + 'sanovaCategory')
                .then(response => {
                    const categories = response.data.sanovaCategory || [];
                    this.sanovaCategories = categories.sort((a, b) => a.name.localeCompare(b.name));
                })
                .catch(e => {
                    console.error('Error loading categories:', e);
                });
        },

        handleClose() {
            this.$emit('close');
        }

    }
};
</script>

<style lang="scss" scoped>

.brand-groups-modal {
    padding: 10px 0;
}

.group-fields-container {
    display: flex;
    flex-direction: column;
    gap: 16px 0;
}

.fields-row {
    display: flex;
    gap: 16px;
    
    .field-item {
        flex: 1;
        margin: 0;
    }
}

.replace-content {
    display: flex;
    gap: 12px;
    margin-top: 6px;

    .preview-container {
        flex-shrink: 0;
    }
    
    .dropzone-container {
        flex: 1;
        ::v-deep .customdropzone {
            height: 125px !important;
            min-height: 125px !important;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
        }
        ::v-deep .dz-message {
            font-size: 11px;
            margin: 0;
        }
    }
}

.preview-box {
    width: 150px;
    height: 125px;
    border: 1px solid #efefef;
    background-color: #fafafa;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px;
    text-align: center;
    border-radius: 4px;

    &.image-preview {
        background-color: #FFF;
        background-size: cover;
        background-position: center !important;
        background-repeat: no-repeat;
    }
}

.empty-state {
    text-align: center;
    padding: 32px 16px;
    color: #aaa;

    i {
        font-size: 32px;
        display: block;
        margin-bottom: 8px;
    }

    p {
        font-size: 14px;
    }
}

</style>
